import { useState, useEffect, useCallback, useContext, useRef } from "react";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import IshaChat from "../components/IshaChat";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// Maps status values to display badge styles
const STATUS_STYLES = {
  COMPLETE:       { bg: "rgba(16,185,129,0.12)", color: "var(--accent-green)",  label: "Complete" },
  FAILED:         { bg: "rgba(239,68,68,0.12)",  color: "var(--accent-red)",    label: "Failed" },
  PROCESSING:     { bg: "rgba(59,130,246,0.12)",  color: "var(--accent-blue)",   label: "Processing" },
  QUEUED:         { bg: "rgba(245,158,11,0.12)",  color: "#f59e0b",             label: "Queued" },
  PENDING_UPLOAD: { bg: "rgba(156,163,175,0.12)", color: "var(--text-muted)",    label: "Uploading" },
};
const DEFAULT_STYLE = { bg: "rgba(156,163,175,0.12)", color: "var(--text-muted)", label: "Pending" };

export default function ScanHistoryPage() {
  const { token } = useContext(AuthContext);
  const [scans, setScans]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [deleting, setDeleting] = useState(null); // job_id being deleted

  // Auto-refresh every 15s if any scan is still in progress
  const intervalRef = useRef(null);
  const hasInProgress = scans.some((s) =>
    !["COMPLETE", "FAILED"].includes(s.status)
  );

  const authHeaders = () => token ? { Authorization: `Bearer ${token}` } : {};

  const fetchScans = useCallback(async () => {
    try {
      const hdrs = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_BASE}/api/scans`, { headers: hdrs });
      setScans(res.data.scans || []);
    } catch {
      setScans([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchScans(); }, [fetchScans]);

  // Poll for updates while there are in-progress scans
  useEffect(() => {
    if (hasInProgress) {
      intervalRef.current = setInterval(fetchScans, 15000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [hasInProgress, fetchScans]);

  const handleDelete = async (jobId) => {
    if (!window.confirm("Delete this scan and all its data? This cannot be undone.")) return;
    setDeleting(jobId);
    try {
      await axios.delete(`${API_BASE}/api/scans/${jobId}`, { headers: authHeaders() });
      setScans((prev) => prev.filter((s) => s.job_id !== jobId));
    } catch {
      alert("Failed to delete scan. Please try again.");
    } finally {
      setDeleting(null);
    }
  };

  const handleView = (jobId) => {
    // Navigate to lung cancer page with job_id to view results
    window.location.href = `/lung-cancer?job=${jobId}`;
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <>
      <h1 className="h3 mb-3"><strong>Scan History</strong></h1>
      <p style={{ color: "var(--text-secondary)", marginBottom: "1.5rem" }}>
        All your CT scan analyses are tracked here automatically. Come back anytime to check progress or view completed results.
      </p>

      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <div className="spinner" style={{ width: 32, height: 32, margin: "0 auto 1rem" }} />
          <p style={{ color: "var(--text-muted)" }}>Loading scan history…</p>
        </div>
      ) : scans.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "3rem" }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" style={{ marginBottom: 12 }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14,2 14,8 20,8"/>
          </svg>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>No scans yet</p>
          <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
            Upload a CT scan to start an analysis. All your jobs will appear here automatically.
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
            <thead>
              <tr style={{ background: "var(--bg-input)", borderBottom: "1px solid var(--border)" }}>
                <th style={thStyle}>Patient</th>
                <th style={thStyle}>Nodules</th>
                <th style={thStyle}>Slices</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scans.map((scan) => (
                <tr key={scan.job_id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: 500 }}>{scan.patient_name || "Anonymous"}</span>
                    <br />
                    <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{scan.filename}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{
                      fontWeight: 600,
                      color: scan.nodule_count > 0 ? "var(--accent-red)" : scan.nodule_count === 0 ? "var(--accent-green)" : "var(--text-muted)",
                    }}>
                      {scan.nodule_count != null ? scan.nodule_count : "—"}
                    </span>
                  </td>
                  <td style={tdStyle}>{scan.total_slices || "—"}</td>
                  <td style={tdStyle}>{fmtDate(scan.created_at)}</td>
                  <td style={tdStyle}>
                    {(() => {
                      const st = STATUS_STYLES[scan.status] || DEFAULT_STYLE;
                      return (
                        <span style={{
                          padding: "2px 8px",
                          borderRadius: 10,
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          background: st.bg,
                          color: st.color,
                        }}>
                          {st.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button
                        className="btn btn-primary"
                        style={{ padding: "4px 12px", fontSize: "0.78rem" }}
                        onClick={() => handleView(scan.job_id)}
                      >
                        View
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{
                          padding: "4px 12px",
                          fontSize: "0.78rem",
                          color: "#eee4e4",
                          background: "#0099ff",
                          borderColor: "#0099ff",
                        }}
                        onClick={() => handleDelete(scan.job_id)}
                        disabled={deleting === scan.job_id}
                      >
                        {deleting === scan.job_id ? "…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <IshaChat />
    </>
  );
}

const thStyle = {
  padding: "10px 14px",
  textAlign: "left",
  fontSize: "0.78rem",
  fontWeight: 600,
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const tdStyle = {
  padding: "12px 14px",
  verticalAlign: "middle",
};
